import { CaretDownIcon, UsersIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { TextField } from "@/components/TextField/TextField";
import { cabinClassLabels } from "@/features/flights/utils/cabinClasses";
import {
  FlightSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";

import { TravellersSelection } from "../components/PassengersSelection";
import type { FlightSearchForm } from "../FlightSearchForm";

type FlightTravelersPickerProps = {
  form: FlightSearchForm;
  analyticsSource: SearchSource;
};

export function FlightTravelersPicker({
  form,
  analyticsSource,
}: FlightTravelersPickerProps) {
  const intl = useIntl();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [adult, child, infantWithoutSeat, cabinClass] = useWatch({
    control: form.control,
    name: ["adult", "child", "infantWithoutSeat", "cabinClass"],
  });

  const travellersCount = adult + child + infantWithoutSeat;

  return (
    <>
      <TextField
        name="passengersNo"
        fullWidth
        id="passengersNo"
        label={intl.formatMessage({
          defaultMessage: "Passengers, Cabin class",
          description: "Short summary label showing passengers and cabin class",
        })}
        className="w-full p-0"
        startAdornment={<UsersIcon />}
        endAdornment={<CaretDownIcon />}
        value={intl.formatMessage(
          {
            defaultMessage:
              "{travellersCount, plural, one {# person} other {# people}}, {cabinClass}",
            description: "Compact summary of passenger count and cabin class",
          },
          {
            travellersCount,
            cabinClass: intl.formatMessage(cabinClassLabels[cabinClass]),
          },
        )}
        onClick={() => {
          rudderAnalytics.track(
            FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_TRAVELLERS_INPUT,
            {
              source: analyticsSource,
            },
          );

          setIsDrawerOpen(true);
        }}
      />

      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
      >
        <DrawerTopBar
          title={intl.formatMessage({
            defaultMessage: "Passengers & cabin class",
            description:
              "Label for the combined passengers and cabin class picker",
          })}
          onCloseClick={() => setIsDrawerOpen(false)}
        />

        <DrawerContent>
          <TravellersSelection
            initialValues={{
              adult,
              child,
              infantWithoutSeat,
              cabinClass,
            }}
            onConfirm={(values) => {
              rudderAnalytics.track(
                FlightSearchTrackingEvents.USER_CHANGED_TRAVELLERS_AND_CLASS,
                {
                  travellers: {
                    adults: values.adult,
                    children: values.child,
                    infants: values.infantWithoutSeat,
                  },
                  class: values.cabinClass,
                  source: analyticsSource,
                },
              );

              form.reset({
                ...form.getValues(),
                ...values,
              });
              setIsDrawerOpen(false);
            }}
          />
        </DrawerContent>
      </SwipeableDrawer>
    </>
  );
}
